import { type FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetCarrierList } from '@views/screens/Shipment';
import type { FormItemProps, SelectProps } from 'antd';
import i18n from 'i18next';

import { ReactComponent as ZoomIcon } from './../../../../assets/icons/zoom-input.svg';
import CarrierSearchModal from '../../../screens/common/CarrierSearchModal/CarrierSearchModal';
import IconButton from '../../Buttons/IconButton';
import SvgConverter from '../../Common/SvgConverter';
import FormSelectBox from '../../Inputs/FormSelectBox';

interface CarrierSearchFormInitialValues {
  CarrierName?: string;
  CarrierScacCode?: string;
  postalCode?: string;
  countryCode?: string;
  stateCode?: string;
  city?: string;
}

interface TransportationProviderSelectorProps
  extends Pick<FormItemProps, 'rules' | 'name'>,
    Pick<SelectProps, 'placeholder'> {
  label?: string;
  form?: any;
  disabled?: boolean;
  margin?: string;
  editMode?: boolean;
  inputValue?: number;
  defaultOptions?: string[];
  defaultValue?: string;
  onChange?: (value?: unknown) => void;
  hasError?: boolean;
  showPopupFilter?: boolean;
  carrierName?: string;
  disableOpen?: boolean;
  tabIndex?: number;
  labelForTestId?: string;
  customerKey?: string;
  isAllRequired?: boolean;
  isVendor?: boolean;
  isRequired?: boolean;
  initialValues?: CarrierSearchFormInitialValues;
}

const TransportationProviderSelector: FC<TransportationProviderSelectorProps> = ({
  placeholder = i18n.t('translation:default.fields.transportationProvider.selectPlaceholder'),
  label = i18n.t('translation:default.fields.transportationProvider.label'),
  rules = [],
  name,
  form,
  disabled = false,
  margin = 'mb-0',
  editMode = true,
  inputValue,
  defaultOptions,
  defaultValue,
  onChange,
  hasError = false,
  showPopupFilter = false,
  carrierName,
  disableOpen = false,
  tabIndex,
  labelForTestId,
  customerKey,
  isAllRequired = false,
  isVendor = false,
  isRequired = false,
  initialValues,
}) => {
  const [modal, setModal] = useState(false);
  const { selectedCompany } = useSelector((state: any) => state.auth);
  const [allowClear, setAllowClear] = useState(!showPopupFilter);

  const customerKeyToUse = isVendor ? customerKey : selectedCompany?.companyId;
  const { data: carrierList = [], isLoading: loading } = useGetCarrierList({ customerKey: customerKeyToUse });

  const options = useMemo(() => {
    let providerOptions: { label: string; value: number | string }[] = (carrierList || []).map((row) => ({
      label: row.carrierName,
      value: row.carrierKey,
    }));
    if (isAllRequired) {
      providerOptions = [{ label: 'All', value: '0' }, ...providerOptions];
    }
    const isExist = providerOptions.find((option) => option.value === inputValue);
    if (!isExist && carrierName && inputValue) {
      providerOptions = [{ label: carrierName, value: inputValue }, ...providerOptions];
    }
    return providerOptions;
  }, [carrierList, isAllRequired, inputValue, carrierName]);

  // @ts-expect-error
  const handleSelect = (data) => {
    if (onChange) {
      onChange(data);
    } else if (form) {
      form.setFieldValue(name, data?.carrierKey);
    }
    setModal(false);
  };

  const onChangeValue = (data: { label: string; value: number } | unknown) => {
    setAllowClear(true);
    if (onChange) {
      onChange(data);
    }
    if (form && !form.getFieldValue(name)) {
      setAllowClear(false);
    }
  };

  return (
    <>
      <FormSelectBox
        disableOpen={disableOpen}
        loading={loading}
        options={options}
        placeholder={placeholder}
        label={label}
        margin={margin}
        isRequired={isRequired}
        rules={rules}
        name={name}
        editMode={editMode}
        defaultValue={defaultValue}
        value={inputValue ? inputValue : form?.getFieldValue?.(name)}
        onClear={() => form?.setFieldsValue?.({ [name]: undefined })}
        hasError={hasError}
        defaultOptions={defaultOptions}
        disabled={disabled}
        allowClear={allowClear}
        {...(!allowClear
          ? {
              suffixIcon: (
                <IconButton onClick={() => setModal(true)} tabIndex={disableOpen ? tabIndex : -1}>
                  <SvgConverter Icon={ZoomIcon} color="muted" />
                </IconButton>
              ),
            }
          : {})}
        onChange={onChangeValue}
        tabIndex={!disableOpen ? tabIndex : -1}
        dataTestId={labelForTestId}
      />
      <CarrierSearchModal
        open={modal}
        // @ts-expect-error
        handleSelect={handleSelect}
        handleClose={() => setModal(false)}
        labelForTestId={labelForTestId}
        initialValues={initialValues}
      />
    </>
  );
};

export default TransportationProviderSelector;
